/**
 * Gesprächsprotokolle (ehemals „Anrufhistorie", CRM 2.0 Paket 23.10.2026 a)
 *
 * Gemeinsame Komponente für alle Stellen, die die Telefonate eines Kunden
 * zeigen: Kommunikation-Tab, /pflege-Drawer, /anfragen-Drawer, Dashboard-Karte.
 *
 * Datenquelle: GET /v2/admin/3cx/calls?user_id= (Familienkonto: das Backend
 * liefert die Anrufe ALLER Konto-Mitglieder, egal welche user_id übergeben
 * wird, siehe ThreeCxController::callHistoryUserIds).
 *
 * Jede Zeile lässt sich aufklappen: Player je aufgezeichnetem Gesprächsteil
 * und das Whisper-Transkript. Gibt es keins, steht dort ein klarer
 * Leerzustand („Noch kein Transkript") bzw. der Hinweis, dass die Aufnahme
 * noch transkribiert wird.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui';
import { Icon } from '../icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { formatDateTime } from '../../lib/dateFormat';
import { v2ThreeCx, type ThreeCxCall } from '../../lib/threeCx';

export interface GespraechsprotokolleProps {
  /** users.id irgendeines Kontomitglieds (PB/BK). null → Leerzustand ohne Request. */
  userId: number | null | undefined;
  dark: boolean;
  /** Anzahl Einträge (per_page), Default 50. */
  limit?: number;
  /** 'card' = eigene Karte mit Titel; 'plain' = nur die Liste (für Drawer/Karten). */
  variant?: 'card' | 'plain';
  /** Maximalhöhe der scrollbaren Liste in px (Default 360, 0 = unbegrenzt). */
  maxHeight?: number;
  /** Erhöhen löst ein Neuladen aus (z. B. nach einem Anruf). */
  reloadSignal?: number;
  /** Meldet die geladenen Anrufe nach oben (Dashboard-Zähler). */
  onLoaded?: (calls: ThreeCxCall[], total: number | null) => void;
}

/** Lädt die Gespräche; teilt sich Logik zwischen Liste und Dashboard-Karte. */
export function useGespraechsprotokolle(
  userId: number | null | undefined,
  limit = 50,
  reloadSignal = 0,
) {
  const { t: tr } = useTranslation();
  const [calls, setCalls] = useState<ThreeCxCall[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) { setCalls([]); setTotal(null); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    v2ThreeCx.calls(userId, { per_page: limit })
      .then(p => {
        if (!alive) return;
        setCalls(p.data);
        setTotal(p.meta?.total ?? p.data.length);
      })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.comm.error_load')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, limit, reloadSignal, tr]);

  return { calls, total, loading, error };
}

export function Gespraechsprotokolle({
  userId, dark, limit = 50, variant = 'card', maxHeight = 360, reloadSignal = 0, onLoaded,
}: GespraechsprotokolleProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { calls, total, loading, error } = useGespraechsprotokolle(userId, limit, reloadSignal);

  useEffect(() => { if (!loading) onLoaded?.(calls, total); }, [calls, total, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = error ? (
    <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{error}</div>
  ) : (
    <div data-testid="gespraechsprotokolle-list" style={{ maxHeight: maxHeight > 0 ? maxHeight : undefined, overflowY: maxHeight > 0 ? 'auto' : undefined }}>
      {!loading && calls.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>
          {tr('call_history.empty')}
        </div>
      )}
      {calls.map(c => <CallRow key={c.id} call={c} dark={dark} />)}
    </div>
  );

  if (variant === 'plain') {
    return (
      <div data-testid="gespraechsprotokolle">
        {loading && <div style={{ fontSize: 12, color: t.muted, padding: '4px 0 8px' }}>{tr('common.loading_short')}</div>}
        {body}
      </div>
    );
  }

  return (
    <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>
          {tr('call_history.title')}
        </h3>
        {loading && <span style={{ fontSize: 12, color: t.muted }}>{tr('common.loading_short')}</span>}
      </div>
      {body}
    </Card>
  );
}

/**
 * Eine Zeile der Gesprächsprotokolle. Jedes Gespräch lässt sich aufklappen
 * (feature/3cx-recording-transcription): Player je
 * aufgezeichnetem Gesprächsteil + Whisper-Transkript.
 */
export function CallRow({ call, dark }: { call: ThreeCxCall; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const dateLabel = formatDateTime(call.started_at);

  const durationLabel = call.duration_seconds != null
    ? `${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, '0')}`
    : '—';

  const isMissed = call.status === 'missed';
  const statusLabel = tr(`call_history.status.${call.status}`);
  const statusBg = isMissed
    ? (dark ? '#2a1212' : '#fef3f2')
    : call.status === 'ended'
      ? (dark ? '#0f3a23' : '#ecfdf3')
      : (dark ? '#1a2a3a' : '#eff8ff');
  const statusFg = isMissed
    ? (dark ? '#fca5a1' : '#b42318')
    : call.status === 'ended'
      ? (dark ? '#86efac' : '#027a48')
      : (dark ? '#7dd3fc' : '#026aa2');

  const recordings = call.recordings ?? [];
  const transcriptionStatus = call.transcription_status ?? null;
  // Jedes Gespräch lässt sich aufklappen: auch ohne Aufnahme steht dort der
  // Leerzustand „Noch kein Transkript" (statt einer toten Zeile).
  const expandable = true;
  const toggle = () => setOpen(o => !o);

  return (
    <div style={{
      borderBottom: `1px solid ${t.borderSoft}`,
      background: isMissed ? (dark ? 'rgba(180,35,24,0.06)' : '#fffbfa') : undefined,
    }}>
      <div
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? open : undefined}
        aria-label={expandable ? tr('call_history.transcript.toggle') : undefined}
        onClick={toggle}
        onKeyDown={e => { if (expandable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
        style={{
          padding: '11px 20px',
          display: 'grid',
          gridTemplateColumns: '20px 110px 1fr 60px auto auto',
          alignItems: 'center', gap: 12,
          cursor: expandable ? 'pointer' : undefined,
        }}
      >
        <span title={call.direction === 'inbound' ? tr('call_history.direction.inbound') : tr('call_history.direction.outbound')}>
          {call.direction === 'inbound'
            ? <Icon.Phone c={isMissed ? '#b42318' : t.muted} />
            : <Icon.Phone c={t.muted} />}
        </span>
        <span style={{ fontSize: 12, color: t.muted, fontFamily: 'monospace' }}>{dateLabel}</span>
        <span style={{
          fontSize: 13, color: t.heading, fontWeight: isMissed ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {call.contact_name ?? call.external_number ?? tr('call_history.unknown')}
          {call.admin && <span style={{ color: t.muted, fontWeight: 400 }}> · {call.admin}</span>}
        </span>
        <span style={{ fontSize: 12, color: t.muted, fontFamily: 'monospace', textAlign: 'right' }}>{durationLabel}</span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          background: statusBg, color: statusFg,
          borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap',
        }}>{statusLabel}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 16, justifyContent: 'flex-end' }}>
          {transcriptionStatus && <TranscriptBadge status={transcriptionStatus} dark={dark} />}
          {expandable && <Icon.Chevron dir={open ? 'up' : 'down'} c={t.muted} />}
        </span>
      </div>

      {open && expandable && <CallTranscriptPanel call={call} dark={dark} />}
    </div>
  );
}

function TranscriptBadge({ status, dark }: { status: NonNullable<ThreeCxCall['transcription_status']>; dark: boolean }) {
  const { t: tr } = useTranslation();
  const palette = {
    done:       dark ? ['#1e2a4a', '#a5b4fc'] : ['#eef2ff', '#3538cd'],
    pending:    dark ? ['#2a2a2a', '#d0d5dd'] : ['#f2f4f7', '#475467'],
    processing: dark ? ['#1a2a3a', '#7dd3fc'] : ['#eff8ff', '#026aa2'],
    failed:     dark ? ['#2a1212', '#fca5a1'] : ['#fef3f2', '#b42318'],
  }[status];

  return (
    <span data-testid="transcript-badge" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      background: palette[0], color: palette[1],
      borderRadius: 999, padding: '3px 9px',
    }}>
      <Icon.File c={palette[1]} />
      {tr(`call_history.transcript.status.${status}`)}
    </span>
  );
}

function CallTranscriptPanel({ call, dark }: { call: ThreeCxCall; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const recordings = call.recordings ?? [];

  return (
    <div style={{ padding: '2px 20px 14px 52px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {recordings.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {recordings.map((r, i) => (
            <RecordingPlayer
              key={r.id}
              recordingId={r.id}
              label={recordings.length > 1 ? tr('call_history.recording.play_part', { n: i + 1 }) : tr('call_history.recording.play')}
              dark={dark}
            />
          ))}
        </div>
      )}

      {call.transcript ? (
        <div data-testid="call-transcript" style={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontSize: 13, lineHeight: 1.55, color: t.text,
          background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10,
          padding: '10px 12px', maxHeight: 280, overflowY: 'auto',
        }}>
          {call.transcript}
        </div>
      ) : (
        <div data-testid="transcript-none" style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic' }}>
          {call.transcription_status === 'failed'
            ? tr('call_history.transcript.failed')
            : recordings.length > 0
              ? tr('call_history.transcript.pending_hint')
              : tr('call_history.transcript.none')}
        </div>
      )}

      {call.transcript && (
        <div style={{ fontSize: 11, color: t.muted }}>{tr('call_history.transcript.disclaimer')}</div>
      )}
    </div>
  );
}

/**
 * Lädt den presigned Link erst beim Klick (15 min gültig). Läuft er ab,
 * meldet das <audio>-Element einen Fehler und der Knopf erscheint wieder.
 */
function RecordingPlayer({ recordingId, label, dark }: { recordingId: number; label: string; dark: boolean }) {
  const { t: tr } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    setFailed(false);
    try {
      const res = await v2ThreeCx.recordingAudioUrl(recordingId);
      setSrc(res.url);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (src) {
    return (
      <audio
        data-testid="recording-audio"
        controls
        autoPlay
        preload="none"
        src={src}
        onClick={e => e.stopPropagation()}
        onError={() => { setSrc(null); setFailed(true); }}
        style={{ width: '100%', maxWidth: 420, height: 36 }}
      />
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        onClick={e => void load(e)}
        disabled={loading}
        style={{
          fontSize: 12.5, fontWeight: 600, fontFamily: 'Outfit,sans-serif',
          background: dark ? '#1e2a4a' : BRAND_LIGHT, color: dark ? '#c7d2fe' : BRAND,
          border: 'none', borderRadius: 8, padding: '6px 12px',
          cursor: loading ? 'wait' : 'pointer',
        }}
      >
        {loading ? '…' : `▶ ${label}`}
      </button>
      {failed && <span style={{ fontSize: 12, color: '#b42318' }}>{tr('call_history.recording.error')}</span>}
    </span>
  );
}
